import { useFetchPatientsApi, usePostPatientApi, usePutPatientApi } from 'api/patient'
import { Flex } from 'components/shared/flex'
import { DateField, InputField, SelectField } from 'components/shared/input'
import { GlobalStateContext } from 'contexts/global_state_context'
import { useContext, useEffect, useState } from 'react'
import { Button, Collapse, Modal } from 'react-bootstrap'
import { Form, useEffectSkipFirst, useForm } from 'utils/hooks'
import { ApiError } from 'utils/network/api_hooks'
import { GENDER_SELECT_ITEM, Patient, PatientForm, PatientSearchForm } from 'entities/patient'
import { AddressFormView } from 'components/address/address_form'
import SelectPatientModal from './select_patient_modal'

type UserFormViewProps = {
    form: Form<PatientForm>
    apiError: ApiError
    skipDuplicateCheck?: boolean
}

export const UserFormView = ({ form, apiError, skipDuplicateCheck }: UserFormViewProps) => {
    const globalState = useContext(GlobalStateContext)
    // const institutionsApi = useFetchInstitutionsApi()
    const searchForm = useForm<PatientSearchForm>({})
    const patientsApi = useFetchPatientsApi(searchForm)
    const [showSelectPatient, setShowSelectPatient] = useState<boolean>(false)

    useEffect(() => {
        // institutionsApi.execute()
    }, [])

    const handleClickCheck = () => {
        if (form.object.familyNameKana && form.object.givenNameKana && form.object.birthday) {
            searchForm.update((f) => {
                f.familyNameKana = form.object.familyNameKana
                f.givenNameKana = form.object.givenNameKana
                f.gender = form.object.gender
                f.birthdayEq = form.object.birthday
            })
        } else {
            globalState.setNotificationMessage({ body: '必須項目を入力してください', colorType: 'danger' })
        }
    }

    useEffectSkipFirst(() => {
        globalState.setLoading(patientsApi.loading)
        if (patientsApi.isSuccess()) {
            if (patientsApi.response.results.length > 0) {
                setShowSelectPatient(true)
                globalState.setNotificationMessage({
                    body: '名前・性別・生年月日が同じユーザーが見つかりました。同一人物がいる場合はチェックしてください',
                    colorType: 'success',
                })
            } else {
                globalState.setNotificationMessage({
                    body: '名前・性別・生年月日が同じユーザーはいませんでした',
                    colorType: 'success',
                })
                form.updateObject('isDuplicateChecked', true)
            }
        }
    }, [patientsApi.loading])

    useEffectSkipFirst(() => {
        if (!showSelectPatient) {
            form.updateObject('isDuplicateChecked', true)
        }
    }, [showSelectPatient])

    useEffectSkipFirst(() => {
        if (!canDuplicateCheck()) {
            form.updateObject('isDuplicateChecked', false)
        }
    }, [form.object.familyName, form.object.givenName, form.object.familyNameKana, form.object.givenNameKana, form.object.gender, form.object.birthday])

    const canDuplicateCheck = () => {
        return [
            form.object.familyName,
            form.object.givenName,
            form.object.familyNameKana,
            form.object.givenNameKana,
            form.object.gender,
            form.object.birthday,
        ].every((i) => !!i)
    }
    // useEffect(() => {
    //     form.updateObject('isDuplicateChecked', false)
    // }, [form.object.familyName, form.object.givenName, form.object.familyNameKana, form.object.givenNameKana, form.object.birthday])

    return (
        <>
            <SelectPatientModal
                openButtonText=""
                executeButtonText="決定"
                show={showSelectPatient}
                onHide={() => setShowSelectPatient(false)}
                onClick={(patients) => {
                    if (patients.length == 0) {
                        globalState.setNotificationMessage({
                            body: '同一人物がいる場合は選択してください。いない場合は右上のxボタンで閉じてください',
                            colorType: 'danger',
                        })
                        return
                    }
                    setShowSelectPatient(false)
                    const patient = patients[0]
                    form.update((f) => {
                        f.address = patient.address
                        f.id = patient.id
                    })
                }}
                items={patientsApi.response.results}
                maxItemCount={1}
            ></SelectPatientModal>
            {/* <SelectField
                form={form}
                attr="institutionId"
                apiError={apiError}
                label="病院・クリニック"
                required
                selectItems={institutionsApi.response.results.map((i) => {
                    return { label: i.name, value: i.id! }
                })}
                includeBlank
            /> */}
            <Flex>
                <InputField form={form} attr="familyName" apiError={apiError} label="姓" required placeholder="例) 山田" />
                <InputField form={form} attr="givenName" apiError={apiError} label="名" required placeholder="例) 太郎" />
            </Flex>
            <Flex>
                <InputField form={form} attr="familyNameKana" apiError={apiError} label="せい" required placeholder="例) やまだ" />
                <InputField form={form} attr="givenNameKana" apiError={apiError} label="めい" required placeholder="例) たろう" />
            </Flex>
            <Flex>
                <SelectField form={form} attr="gender" apiError={apiError} label="性別" selectItems={GENDER_SELECT_ITEM} includeBlank required />
                <DateField form={form} attr="birthday" apiError={apiError} label="生年月日" required showYearDropdown maxDate={new Date()} />
            </Flex>
            {!form.object.isDuplicateChecked && (
                <Button onClick={handleClickCheck} style={{ width: '100%', marginTop: 20 }} disabled={!canDuplicateCheck()}>
                    データベースから同一人物をチェック
                </Button>
            )}
            {
                <Collapse in={form.object.isDuplicateChecked || skipDuplicateCheck}>
                    <div>
                        <Flex>
                            <InputField form={form} attr="email" apiError={apiError} label="メールアドレス" />
                            <InputField form={form} attr="tel" apiError={apiError} label="電話番号(ハイフンあり)" placeholder="xx-xxxx-xxxx" />
                        </Flex>
                        <AddressFormView form={form} apiError={apiError} />
                    </div>
                </Collapse>
            }
        </>
    )
}

type NewPatientFormProps = {
    onComplete?: () => void
}

export const NewPatientForm = (props: NewPatientFormProps): JSX.Element => {
    const globalState = useContext(GlobalStateContext)
    const api = usePostPatientApi()
    const form = useForm<PatientForm>({ address: {} })
    const [show, setShow] = useState<boolean>(false)

    const handleUpdate = () => {
        api.execute(form)
    }

    useEffectSkipFirst(() => {
        globalState.setLoading(api.loading)
        if (api.isSuccess()) {
            if (props.onComplete) {
                setShow(false)
                props.onComplete()
            }
        }
    }, [api.loading])

    const handleClose = () => {
        setShow(false)
    }

    return (
        <>
            <Button
                variant="success"
                onClick={() => {
                    setShow(true)
                }}
            >
                新規登録
            </Button>
            <Modal show={show} onHide={handleClose}>
                <Modal.Header closeButton>
                    <Modal.Title>Patientの登録</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <UserFormView form={form} apiError={api.apiError} skipDuplicateCheck />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose}>
                        閉じる
                    </Button>
                    <Button variant="success" onClick={handleUpdate}>
                        登録
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    )
}
type EditPatientFormProps = {
    patient: Patient
    show: boolean
    onClose: () => void
    onComplete?: () => void
}

export const EditPatientForm = (props: EditPatientFormProps): JSX.Element => {
    const globalState = useContext(GlobalStateContext)
    const api = usePutPatientApi()
    const form = useForm<PatientForm>(props.patient)

    useEffectSkipFirst(() => {
        form.set(props.patient)
    }, [props.patient])

    const handleUpdate = () => {
        api.execute(form)
    }

    useEffectSkipFirst(() => {
        globalState.setLoading(api.loading)
        if (api.isSuccess()) {
            if (props.onComplete) {
                props.onComplete()
            }
        }
    }, [api.loading])

    return (
        <Modal show={props.show} onHide={props.onClose}>
            <Modal.Header closeButton>
                <Modal.Title>Patientの編集</Modal.Title>
            </Modal.Header>
            <Modal.Body>{form.object.id && <UserFormView form={form} apiError={api.apiError} />}</Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={props.onClose}>
                    閉じる
                </Button>
                <Button variant="success" onClick={handleUpdate}>
                    更新
                </Button>
            </Modal.Footer>
        </Modal>
    )
}
